import { useCookies } from "@vueuse/integrations/useCookies";
import { callRefreshToken } from "@skyairline/util-login-api";

declare const environment: string;

const cookieKeys = [
  "token",
  "skyToken",
  "skyUser",
  "refreshToken",
  "userProfile",
  "digitalChannelUser",
];
export const cookie = useCookies(cookieKeys);

const domain = window.location.hostname.includes("skyairline") ? "skyairline.com" : "localhost";
const path = "/";


export function getToken() {
  return cookie.get("token");
}

export function setToken(value: string) {
  return cookie.set("token", value, { domain, path });
}

export function setSkyToken(value: string) {
  return cookie.set("skyToken", value, { domain, path });
}

export function getSkyToken() {
  return cookie.get("skyToken");
}

export function setSkyUser(value: string) {
  return cookie.set("skyUser", JSON.stringify(value), {
    domain,
    path,
  });
}

export function getSkyUser() {
  return cookie.get("skyUser");
}

export function getRefreshToken() {
  return cookie.get("refreshToken");
}

export function setRefreshToken(value: string) {
  return cookie.set("refreshToken", value, { domain, path });
}

export function setUserProfile(profile: string) {
  return cookie.set("userProfile", JSON.stringify(profile), {
    domain,
    path,
  });
}

export function getProfile() {
  return cookie.get("userProfile");
}

export function hasBindingKey() {
  const token = getSkyToken();
  if (!token) {
    return false;
  }
  const tokenParts = token.split(".");
  if (tokenParts.length < 2) {
    return false;
  }

  const encodedToken = tokenParts[1];
  const decodedToken = atob(encodedToken);
  const parsedToken = JSON.parse(decodedToken);

  return parsedToken?.bindingKey || false;
}

export function setByKey(key: string, value: string) {
  return cookie.set(key, value, { domain, path });
}

export function getByKey(key: string) {
  return cookie.get(key);
}

export function removeSkySession() {
  const cookies = cookie.getAll();
  Object.keys(cookies).forEach(function (key) {
    cookie.remove(key, { domain, path });
  });
  window.localStorage.clear();
  window.localStorage.clear();
  return window.location.reload();
}

export function validateCorpoLogin() {
  const businessStore = window.localStorage.getItem("businessStore");
  if (businessStore) {
    try {
      const decoded = atob(businessStore);
      const corpoStore = JSON.parse(JSON.parse(decoded)); // TODO: Improve double parsing logic
      if (
        corpoStore?.userInfo?.portal &&
        ["stateportal", "corporate"].includes(
          corpoStore.userInfo.portal.toLowerCase()
        )
      ) {
        const cookies = cookie.getAll();
        Object.keys(cookies).forEach(function (key) {
          if (cookieKeys.includes(key)) cookie.remove(key, { domain, path });
          return true;
        });
      }
    } catch (error) {
      console.error("Error decoding businessStore", error);
    }
  }
}

export function initLoginStore() {

  validateCorpoLogin();
  const token = getSkyToken() ?? getToken();
  if (token) {
    if (!getProfile() && !getByKey("digitalChannelUser")) removeSkySession();
    window.addEventListener("single-spa:routing-event", (_evt) => {
      validateOrRefreshToken()
    });
  }
}

export function removeToken() {
  return cookie.remove("token", { domain, path });
}

export function removeSkyUser() {
  return cookie.remove("skyUser", { domain, path });
}

export function setEnvironment(env: string) {
  return cookie.set("environment", env, { domain, path });
}

export function validateEnvironment() {
  const currentEnvironment = cookie.get("environment");
  if (currentEnvironment && currentEnvironment !== environment) {
    removeSkySession();
  }
}

export async function validateOrRefreshToken() {
  const TEN_MINUTES_IN_SECONDS = 600
  const token = getSkyToken();
  if (token) {
    const tokenParts = token.split(".");
    if (tokenParts.length < 2) {
      return false;
    }

    const encodedToken = tokenParts[1];
    const decodedToken = atob(encodedToken);
    const parsedToken = JSON.parse(decodedToken);

    const exp = parsedToken.exp;
    const now = Math.floor(Date.now() / 1000);
    const diff = exp - now
    if (diff > 0 && diff < TEN_MINUTES_IN_SECONDS) {
      const refreshToken = getRefreshToken()
      try {
        const { content } = await callRefreshToken(refreshToken)
        setSkyToken(content.token)
      } catch (error) {
        console.error('Error getting refresh token', error)
        removeSkySession()
      }
    } else if (diff <= 0 ) {
      removeSkySession()
    }
  }
}
